import { useEffect, useRef, useState } from 'react';
import { findVerse, type VerseEnv } from '@rb/shared/admin-verses';
import { useActiveVerseStore } from './active-verse.store';

/**
 * 활성 verse 의 env(dev/staging/prod). 등록 verse 만 해석하며,
 * 미등록(custom) 이거나 활성 verse 가 없으면 null. 리액티브 훅.
 */
export function useActiveVerseEnv(): VerseEnv | null {
  return useActiveVerseStore((s) =>
    s.activeVerseId ? (findVerse(s.activeVerseId)?.env ?? null) : null
  );
}

export interface VerseEnvChannel {
  channel: VerseEnv;
  setChannel: (c: VerseEnv) => void;
  /** 자동 동기화 소스인 활성 verse env. 미등록/미연결이면 null. */
  verseEnv: VerseEnv | null;
}

/**
 * 채널(dev/staging/prod) 선택을 활성 verse 의 env 에 자동 동기화하되 수동 선택을 유지한다.
 *
 * - verse env 가 바뀔 때마다 채널을 그 env 로 덮어쓴다(prod verse 선택 → prod 채널).
 * - 그 사이에는 사용자가 드롭다운으로 자유롭게 채널을 바꿀 수 있고, 다음 verse
 *   전환 전까지 그 선택이 유지된다.
 * - 미등록(custom) verse 처럼 env 를 알 수 없을 땐 채널을 건드리지 않는다.
 *
 * GatewaySelector 의 override 가 verse 전환 시 초기화되는 패턴과 동일한 철학.
 */
export function useVerseEnvChannel(): VerseEnvChannel {
  const verseEnv = useActiveVerseEnv();
  const [channel, setChannel] = useState<VerseEnv>(verseEnv ?? 'dev');
  const prevVerseEnv = useRef<VerseEnv | null>(verseEnv);

  useEffect(() => {
    if (verseEnv !== prevVerseEnv.current) {
      prevVerseEnv.current = verseEnv;
      if (verseEnv) setChannel(verseEnv);
    }
  }, [verseEnv]);

  return { channel, setChannel, verseEnv };
}
